/**
 * Channel-specific listing requirements.
 *
 * Adapters decide what the operator must fix for provider + site + category +
 * listing type. Amazon/Temu catalogues exist for projection only — this module
 * never calls those APIs.
 */

export type RequirementLevel = "off" | "required";

export interface ChannelRequirementProfile {
  provider: string;
  identifier: RequirementLevel;
  brand: RequirementLevel;
  mpn: RequirementLevel;
  weight: RequirementLevel;
  dimensions: RequirementLevel;
  images: RequirementLevel;
  titleMaxChars: number | null;
  descriptionRequired: boolean;
}

export interface CategoryConstraintHints {
  identifierRequired?: boolean;
  brandRequired?: boolean;
  mpnRequired?: boolean;
}

export function listingChannelRequirementProfile(opts: {
  provider: string;
  marketplaceSite?: string | null;
  productType?: string | null;
  categoryHints?: CategoryConstraintHints | null;
}): ChannelRequirementProfile {
  const provider = (opts.provider || "").toLowerCase();
  const hints = opts.categoryHints ?? {};

  if (provider === "amazon") {
    // 020C: no guessed Amazon required fields. PTD is the only authority (020D+).
    return {
      provider,
      identifier: "off",
      brand: "off",
      mpn: "off",
      weight: "off",
      dimensions: "off",
      images: "off",
      titleMaxChars: null,
      descriptionRequired: false,
    };
  }

  if (provider === "ebay") {
    return {
      provider,
      identifier: hints.identifierRequired ? "required" : "off",
      brand: hints.brandRequired ? "required" : "off",
      mpn: hints.mpnRequired ? "required" : "off",
      weight: "off",
      dimensions: "off",
      images: "required",
      titleMaxChars: 80,
      descriptionRequired: true,
    };
  }

  if (provider === "bigcommerce") {
    return {
      provider,
      identifier: "off",
      brand: "off",
      mpn: "off",
      weight: "off",
      dimensions: "off",
      images: "off",
      titleMaxChars: 250,
      descriptionRequired: false,
    };
  }

  return {
    provider: provider || "unknown",
    identifier: "off",
    brand: "off",
    mpn: "off",
    weight: "off",
    dimensions: "off",
    images: "off",
    titleMaxChars: null,
    descriptionRequired: false,
  };
}

export function ebayAspectHintsFromDefinitions(definitions: unknown): CategoryConstraintHints {
  const hints: CategoryConstraintHints = {};
  if (!Array.isArray(definitions)) return hints;
  for (const raw of definitions) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const name = String(row.localizedAspectName ?? row.name ?? "").trim().toUpperCase();
    const constraint = (row.aspectConstraint ?? row.constraint ?? {}) as Record<string, unknown>;
    const required = constraint.aspectRequired === true || constraint.required === true;
    if (!required) continue;
    if (["UPC", "EAN", "ISBN", "GTIN"].includes(name)) hints.identifierRequired = true;
    if (name === "BRAND") hints.brandRequired = true;
    if (name === "MPN" || name === "MANUFACTURER PART NUMBER") hints.mpnRequired = true;
  }
  return hints;
}

export function readinessFixPath(code: string, productId?: string | null, listingId?: string | null): string | null {
  switch (code) {
    case "missing_gtin":
      return productId ? `/products/${productId}` : null;
    case "missing_brand":
    case "missing_mpn":
    case "missing_weight":
    case "missing_dimensions":
      return productId ? `/products/${productId}` : null;
    case "missing_category":
    case "no_variants":
    case "title_too_long":
    case "missing_description":
      return listingId ? `/listings/${listingId}` : null;
    case "no_media":
      return productId ? `/products/${productId}?tab=media` : null;
    case "connector_not_connected":
      // Canonical Integrations workspace route (there is no /settings/integrations).
      return "/integrations";
    default:
      return null;
  }
}
